import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AttendanceService } from '../services/attendance.service';
import type { AttendanceRecord, AttendanceFilters } from '../types/attendance.types';
import { useState } from 'react';

export function useAttendance(companyId: string, initialFilters: AttendanceFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<AttendanceFilters>({
    status: 'all',
    date: new Date().toISOString().split('T')[0],
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['attendance', companyId, filters],
    queryFn: () => AttendanceService.getAttendanceRecords(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['attendance-stats', companyId, filters.projectId, filters.date],
    queryFn: () => AttendanceService.getAttendanceStats(companyId, filters.projectId, filters.date),
    enabled: !!companyId,
  });

  const checkInMutation = useMutation({
    mutationFn: (data: Partial<AttendanceRecord>) => AttendanceService.checkIn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-stats'] });
    },
  });

  const checkOutMutation = useMutation({
    mutationFn: ({ id, lat, lng }: { id: string, lat?: number, lng?: number }) => 
      AttendanceService.checkOut(id, lat, lng),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-stats'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<AttendanceRecord> }) => 
      AttendanceService.updateAttendance(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });

  return {
    ...query,
    records: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    checkIn: checkInMutation.mutateAsync,
    checkOut: checkOutMutation.mutateAsync,
    updateAttendance: updateMutation.mutateAsync,
    isCheckingIn: checkInMutation.isPending,
    isCheckingOut: checkOutMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}

export function useAttendanceShifts(companyId: string) {
  return useQuery({
    queryKey: ['attendance-shifts', companyId],
    queryFn: () => AttendanceService.getShifts(companyId),
    enabled: !!companyId,
  });
}
